/**
 * The leave domain's client-side vocabulary — the label/badge maps for a request's
 * `status`, and the one piece of server-refusal parsing this surface needs: reading the
 * over-draw `409`'s numbers back out so they can be shown rather than only named.
 *
 * Nothing here decides anything about a request. The labels turn the contract's enum
 * into `MessageKey`s so the screen never hard-codes a status word (the same discipline
 * `hr/onboarding.ts` and `recruiting/offer.ts` keep), and `insufficientBalanceDetails`
 * reads a refusal the server already sent — it does not compute a balance itself.
 */

import type { MessageKey } from '../i18n/messages';
import type { Severity } from '../states/icons';
import type { WorkforceApiError } from '../api/errors';
import type { LeaveRequestStatus } from '../api/hr';

export { LEAVE_REQUEST_STATUSES } from '../api/hr';
export type { LeaveRequestStatus } from '../api/hr';

/** The name of a request's `status`, for the my-requests and approvals rows. */
export const LEAVE_REQUEST_STATUS_LABEL: Readonly<Record<LeaveRequestStatus, MessageKey>> = {
  pending: 'hr.leave.status.pending',
  approved: 'hr.leave.status.approved',
  rejected: 'hr.leave.status.rejected',
  cancelled: 'hr.leave.status.cancelled',
};

/**
 * The badge severity a request status reads as. `pending` is `info` (in flight,
 * awaiting a decision); `approved` the good terminal (`low`); `rejected` and
 * `cancelled` are terminals to notice (`medium`) — nobody's fault, but not the request
 * that was filed. Icon and text carry the fact; colour is the third channel
 * (DESIGN.md §4).
 */
export const LEAVE_REQUEST_STATUS_BADGE: Readonly<Record<LeaveRequestStatus, Severity>> = {
  pending: 'info',
  approved: 'low',
  rejected: 'medium',
  cancelled: 'medium',
};

/** Only a `pending` request may be decided or withdrawn — the state machine is this flat. */
export function isPendingLeaveRequest(status: LeaveRequestStatus): boolean {
  return status === 'pending';
}

/**
 * `details` of the `409 WORKFORCE_CONFLICT` `leave-service.ts`'s `approveLeaveRequest`
 * raises when `used + days > accrued` (`reason: 'insufficient_balance'`) — read back so
 * the screen can print the three numbers the server refused over, rather than only the
 * generic conflict sentence `ApiErrorNote` already shows. `null` for every other
 * refusal, so a caller can render the extra sentence only when there is one to render.
 */
export interface InsufficientBalanceDetails {
  readonly accruedDays: number;
  readonly usedDays: number;
  readonly requestedDays: number;
}

export function insufficientBalanceDetails(
  error: WorkforceApiError,
): InsufficientBalanceDetails | null {
  if (error.code !== 'WORKFORCE_CONFLICT') return null;
  const { details } = error;
  if (details.reason !== 'insufficient_balance') return null;
  const accrued = details.accrued_days;
  const used = details.used_days;
  const requested = details.requested_days;
  if (typeof accrued !== 'number' || typeof used !== 'number' || typeof requested !== 'number') {
    return null;
  }
  return { accruedDays: accrued, usedDays: used, requestedDays: requested };
}
